/**
 * Trip Lodging — mirrors iOS TripHotelManagementView.
 * List / add / edit / delete hotel stays; live search via LiteAPI.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  List,
  Modal,
  Spin,
  Switch,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessibleTrip, tripWebHref, updateTrip } from '../../services/tripsService';
import { useCanonicalTripUrl } from '../../utils/useCanonicalTripUrl';
import { effectiveCanEdit } from '../../utils/tripAccessPolicy';
import { tripLodgingEn } from '../../content/tripLodging.en';
import type { Trip, TripHotelStay } from '../../types/client';
import {
  allTripHotelStays,
  defaultLodgingDateRange,
  lodgingCityCount,
  lodgingHotelsForAnchor,
  lodgingNamesLabel,
  lodgingPickerAnchors,
} from '../../utils/cruiseLodgingCityVisitFilter';
import {
  confirmationNumbersLabel,
  hotelStayNights,
} from '../../utils/hotelStayHelpers';
import {
  deleteHotelStayFromTrip,
  upsertHotelStayOnTrip,
} from '../../utils/hotelStayMutations';
import { mapsSearchUrl } from '../../utils/preferredMapUrl';
import { loadLocalUserPreferences } from '../../services/userPreferencesSyncService';
import {
  getStoredPlanData,
  hydrateCityPlanFromFile,
} from '../../services/aiPlanPersistService';
import type { AggregatedPlanData } from '../../utils/aggregatedPlanData';
import HotelStayFormModal from '../../components/HotelStayFormModal/HotelStayFormModal';
import HotelLiveSearchPanel from '../../components/HotelLiveSearchPanel/HotelLiveSearchPanel';
import { AccommodationIcon } from '../../components/icons/TripIcons';
import styles from './TripLodging.module.css';

type AddPhase = 'city' | 'search' | 'manual' | null;

const TripLodging: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const [addPhase, setAddPhase] = useState<AddPhase>(null);
  const [showAllPorts, setShowAllPorts] = useState(false);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  const [editStay, setEditStay] = useState<(TripHotelStay & { cityVisitId: string }) | null>(
    null
  );
  const [plan, setPlan] = useState<AggregatedPlanData | null>(null);

  const reload = useCallback((): void => {
    if (!user?.sub || !tripId) {
      setTrip(null);
      setLoading(false);
      return;
    }
    setTrip(getAccessibleTrip(user.sub, tripId));
    setLoading(false);
  }, [user?.sub, tripId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useCanonicalTripUrl(trip);

  const canEdit = Boolean(trip && user?.sub && effectiveCanEdit(trip, user.sub));
  const preferredMapApp = loadLocalUserPreferences().appUserSettings.preferredMapApp;

  const stays = useMemo(() => (trip ? allTripHotelStays(trip) : []), [trip]);
  const cityCount = trip ? lodgingCityCount(trip) : 0;

  const grouped = useMemo(() => {
    const map = new Map<string, typeof stays>();
    for (const s of stays) {
      const list = map.get(s.cityName) ?? [];
      list.push(s);
      map.set(s.cityName, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [stays]);

  const pickerAnchors = useMemo(
    () => (trip ? lodgingPickerAnchors(trip, showAllPorts) : []),
    [trip, showAllPorts]
  );

  const dateRange = useMemo(() => {
    if (!trip || !selectedCityId) {
      return { checkIn: '', checkOut: '' };
    }
    return defaultLodgingDateRange(trip, selectedCityId);
  }, [trip, selectedCityId]);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      if (!trip || !selectedCityId) {
        setPlan(null);
        return;
      }
      let next = getStoredPlanData(trip, selectedCityId);
      if (!next) {
        next = await hydrateCityPlanFromFile(trip.id, selectedCityId);
      }
      if (!cancelled) setPlan(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [trip, selectedCityId]);

  const persist = (updates: Partial<Omit<Trip, 'id'>>): void => {
    if (!user?.sub || !trip) return;
    const updated = updateTrip(user.sub, trip.id, updates);
    if (updated) setTrip(updated);
  };

  const handleSaveStay = (
    values: {
      id: string;
      hotelName: string;
      address?: string;
      phone?: string;
      checkInDate: string;
      checkOutDate: string;
      notes?: string;
      confirmations: TripHotelStay['confirmations'];
    },
    cityVisitId: string,
    previousCityVisitId?: string | null,
    extra?: Partial<TripHotelStay>
  ): void => {
    if (!trip) return;
    const stay: TripHotelStay = {
      ...(editStay ?? {}),
      ...extra,
      id: values.id,
      hotelName: values.hotelName,
      address: values.address ?? null,
      phone: values.phone ?? null,
      checkInDate: values.checkInDate,
      checkOutDate: values.checkOutDate,
      notes: values.notes ?? null,
      confirmations: values.confirmations,
    };
    const patch = upsertHotelStayOnTrip({
      trip,
      cityVisitId,
      stay,
      previousCityVisitId,
    });
    persist(patch);
    message.success(tripLodgingEn.save);
    setEditStay(null);
    setAddPhase(null);
    setSelectedCityId(null);
  };

  const handleDeleteStay = (cityVisitId: string, hotelStayId: string): void => {
    if (!trip) return;
    persist(deleteHotelStayFromTrip({ trip, cityVisitId, hotelStayId }));
    setEditStay(null);
    message.success(tripLodgingEn.deleteStay);
  };

  const tripHref = trip && user?.sub ? tripWebHref(user.sub, trip) : '/trips';

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spin />
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="error" message={tripLodgingEn.missingTrip} showIcon />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.topBar}>
          <Button
            type="link"
            className={styles.backLink}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(tripHref)}
          >
            {tripLodgingEn.back}
          </Button>
        </div>

        <div className={styles.headerCard}>
          <h1 className={styles.tripName}>{trip.title || tripLodgingEn.pageTitle}</h1>
          <p className={styles.subtitle}>{tripLodgingEn.lodgingManagement}</p>
          <p className={styles.stayCount}>
            {tripLodgingEn.stayCount(stays.length, cityCount || trip.cityIds?.length || 0)}
          </p>
        </div>

        {!canEdit ? (
          <Alert
            type="info"
            message={tripLodgingEn.readOnly}
            showIcon
            className={styles.statusBanner}
          />
        ) : (
          <div className={styles.toolbar}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setShowAllPorts(false);
                setAddPhase('city');
              }}
            >
              {tripLodgingEn.addHotel}
            </Button>
          </div>
        )}

        {stays.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon} aria-hidden>
              <AccommodationIcon size={48} />
            </div>
            <p className={styles.emptyTitle}>{tripLodgingEn.emptyTitle}</p>
            <p className={styles.emptyDesc}>{tripLodgingEn.emptyDescription}</p>
            {canEdit ? (
              <Button
                type="primary"
                onClick={() => {
                  setShowAllPorts(false);
                  setAddPhase('city');
                }}
              >
                {tripLodgingEn.addHotel}
              </Button>
            ) : null}
          </div>
        ) : (
          grouped.map(([cityName, cityStays]) => (
            <section key={cityName} className={styles.citySection}>
              <h2 className={styles.cityTitle}>{cityName}</h2>
              {cityStays.map((stay) => {
                const nights = hotelStayNights(stay.checkInDate, stay.checkOutDate);
                const conf = confirmationNumbersLabel(stay.confirmations);
                const mapHref = mapsSearchUrl({
                  preferredMapApp,
                  query: stay.address || stay.hotelName,
                  name: stay.hotelName,
                  latitude: stay.latitude,
                  longitude: stay.longitude,
                });
                return (
                  <article key={stay.id} className={styles.stayCard}>
                    <div className={styles.stayHeader}>
                      <div style={{ flex: 1 }}>
                        <p className={styles.stayName}>{stay.hotelName}</p>
                        <p className={styles.stayMeta}>
                          {(stay.checkInDate || '—').slice(5)} –{' '}
                          {(stay.checkOutDate || '—').slice(5)}
                          {nights > 0 ? ` · ${tripLodgingEn.nights(nights)}` : ''}
                        </p>
                      </div>
                      {canEdit ? (
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          aria-label={tripLodgingEn.editHotel}
                          onClick={() => setEditStay(stay)}
                        />
                      ) : null}
                    </div>
                    {stay.notes ? (
                      <p className={styles.notesPreview}>
                        <strong>{tripLodgingEn.notes}</strong> {stay.notes}
                      </p>
                    ) : null}
                    {conf ? (
                      <div className={styles.confRow}>
                        <span className={styles.confLabel}>{tripLodgingEn.conf}</span>
                        <span>{conf}</span>
                      </div>
                    ) : null}
                    <div className={styles.actions}>
                      {mapHref ? (
                        <Button
                          size="small"
                          icon={<EnvironmentOutlined />}
                          href={mapHref}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {tripLodgingEn.map}
                        </Button>
                      ) : null}
                      {canEdit ? (
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            Modal.confirm({
                              title: tripLodgingEn.deleteConfirm,
                              okType: 'danger',
                              onOk: () => handleDeleteStay(stay.cityVisitId, stay.id),
                            });
                          }}
                        >
                          {tripLodgingEn.deleteStay}
                        </Button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </section>
          ))
        )}
      </div>

      <Modal
        open={addPhase === 'city'}
        title={tripLodgingEn.selectCity}
        onCancel={() => {
          setAddPhase(null);
          setSelectedCityId(null);
        }}
        footer={null}
      >
        {trip.tripType === 'cruise' ? (
          <div style={{ marginBottom: 12 }}>
            <Switch checked={showAllPorts} onChange={setShowAllPorts} />{' '}
            {tripLodgingEn.showAllPorts}
            {!showAllPorts ? (
              <p className={styles.stayAreaMeta}>{tripLodgingEn.prePostHint}</p>
            ) : null}
          </div>
        ) : null}
        <List
          dataSource={pickerAnchors}
          locale={{ emptyText: tripLodgingEn.missingCity }}
          renderItem={(anchor) => {
            const hotels = lodgingHotelsForAnchor(trip, anchor.id);
            const lodgingLabel = lodgingNamesLabel(hotels);
            return (
              <List.Item
                onClick={() => {
                  setSelectedCityId(anchor.id);
                  setAddPhase('search');
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className={styles.cityPickerRow}>
                  <span className={styles.cityPickerName}>{anchor.cityName}</span>
                  <span className={styles.cityPickerMeta}>
                    {anchor.arrivalDate || '—'}
                    {lodgingLabel
                      ? ` · ${tripLodgingEn.lodgingAlready}: ${lodgingLabel}`
                      : ''}
                  </span>
                </div>
              </List.Item>
            );
          }}
        />
      </Modal>

      <Modal
        open={addPhase === 'search' && Boolean(selectedCityId)}
        title={tripLodgingEn.searchHotels}
        onCancel={() => {
          setAddPhase(null);
          setSelectedCityId(null);
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {selectedCityId ? (
          <HotelLiveSearchPanel
            trip={trip}
            cityVisitId={selectedCityId}
            cityName={trip.cityNames?.[selectedCityId] || tripLodgingEn.unknownCity}
            plan={plan}
            checkIn={dateRange.checkIn}
            checkOut={dateRange.checkOut}
            canEdit={canEdit}
            currencyCode={trip.currency}
            onEnterManually={() => setAddPhase('manual')}
            onSaved={(stay) => {
              handleSaveStay(
                {
                  id: stay.id,
                  hotelName: stay.hotelName,
                  address: stay.address ?? undefined,
                  phone: stay.phone ?? undefined,
                  checkInDate: stay.checkInDate || dateRange.checkIn,
                  checkOutDate: stay.checkOutDate || dateRange.checkOut,
                  notes: stay.notes ?? undefined,
                  confirmations: stay.confirmations ?? [],
                },
                selectedCityId,
                null,
                stay
              );
            }}
          />
        ) : null}
      </Modal>

      <HotelStayFormModal
        open={addPhase === 'manual' && Boolean(selectedCityId)}
        mode="add"
        defaultCheckIn={dateRange.checkIn}
        defaultCheckOut={dateRange.checkOut}
        onClose={() => {
          setAddPhase(null);
          setSelectedCityId(null);
        }}
        onSearchInstead={() => setAddPhase('search')}
        onSave={(values) => {
          if (!selectedCityId) return;
          handleSaveStay(values, selectedCityId);
        }}
      />

      <HotelStayFormModal
        open={Boolean(editStay)}
        mode="edit"
        initial={editStay}
        defaultCheckIn={editStay?.checkInDate || dateRange.checkIn}
        defaultCheckOut={editStay?.checkOutDate || dateRange.checkOut}
        canDelete={canEdit}
        onClose={() => setEditStay(null)}
        onDelete={() => {
          if (editStay) handleDeleteStay(editStay.cityVisitId, editStay.id);
        }}
        onSave={(values) => {
          if (!editStay) return;
          handleSaveStay(values, editStay.cityVisitId, editStay.cityVisitId);
        }}
      />
    </div>
  );
};

export default TripLodging;
